<template>
  <div class="br-page py-3">
    <header class="br-toolbar">
      <div class="br-toolbar__title">
        <span class="br-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Branches</h1>
          <p>
            Physical outlets — global, shared across all organisations.
            Each branch belongs to one state.
          </p>
        </div>
      </div>
      <div class="br-toolbar__actions">
        <CButton color="primary" size="sm" :disabled="!states.length" @click="openCreateModal">+ Add Branch</CButton>
        <CButton
          size="sm"
          class="refresh-btn"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="loadAll"
        >
          {{ isLoading ? 'Refreshing…' : 'Refresh' }}
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="br-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="br-alert">{{ successMessage }}</CAlert>
    <CAlert v-if="!states.length && !isLoading" color="warning" show class="br-alert">
      No states defined yet. Add states under <router-link to="/settings/states">Settings → States</router-link> before creating branches.
    </CAlert>

    <section class="br-section">
      <div class="br-section__body">
        <div class="br-filters">
          <div class="br-filter">
            <label class="br-label">Filter by state</label>
            <AppDropdown
              v-model="filterStateId"
              :options="stateFilterOptions"
              placeholder="All states"
            />
          </div>
        </div>
      </div>
    </section>

    <BaseTable
      title="All Branches"
      :items="filteredRows"
      :columns="columns"
      :loading="isLoading"
      :default-sort="{ column: 'name', direction: 'asc' }"
      striped
      hover
      bordered
      sticky-header
      empty-text="No branches yet — add Kondoty, Calicut, etc."
    >
      <template #cell-image="{ row }">
        <span class="br-thumb">
          <img v-if="row.image_url" :src="row.image_url" :alt="`${row.name} image`" @error="row.image_url = null" />
          <span v-else class="br-thumb__initial" aria-hidden="true">{{ branchInitial(row.name) }}</span>
        </span>
      </template>
      <template #cell-state="{ row }">
        <span class="br-state-pill">{{ row.stateName || '—' }}</span>
      </template>
      <template #cell-actions="{ row }">
        <div class="br-row-actions">
          <button type="button" class="br-action-btn br-action-btn--edit" @click="openEditModal(row)">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            <span>Edit</span>
          </button>
          <button type="button" class="br-action-btn br-action-btn--delete" @click="openDeleteConfirm(row)">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
              <path d="M10 11v6"/>
              <path d="M14 11v6"/>
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
            </svg>
            <span>Delete</span>
          </button>
        </div>
      </template>
    </BaseTable>

    <CModal
      :show.sync="showModal"
      size="md"
      :centered="true"
      :title="modalTitle"
      class="br-modal"
      :close-on-backdrop="!isSaving"
    >
      <div class="br-modal__body">
        <div v-if="modalError" class="br-modal__alert">{{ modalError }}</div>
        <div class="br-grid">
          <div class="br-field br-field--wide">
            <AppInput
              no-margin
              label="Branch name"
              required
              :value="form.name"
              :disabled="isSaving"
              placeholder="e.g. Kondoty"
              @update:value="form.name = $event"
            />
          </div>
          <div class="br-field br-field--wide">
            <label class="br-label">State <span class="br-req">*</span></label>
            <AppDropdown
              v-model="form.state_id"
              :options="stateOptions"
              :disabled="isSaving"
              searchable
              search-placeholder="Search state…"
              placeholder="Select state"
            />
          </div>
          <div class="br-field br-field--wide">
            <label class="br-label">Image</label>
            <OrganisationLogoUploader
              v-model="form.image_url"
              :disabled="isSaving"
              upload-path="/api/branch/upload-image"
              form-field="image"
              :alt="form.name ? `${form.name} image` : 'Branch image'"
              prompt-title="Drop branch image or click to upload"
              prompt-hint="PNG, JPG, WebP or SVG · up to 4 MB"
              error-fallback="Branch image upload failed. Check that R2 is configured and try again."
            />
          </div>
        </div>
      </div>
      <template #footer>
        <button type="button" class="br-btn br-btn--ghost" :disabled="isSaving" @click="closeModal">Cancel</button>
        <button type="button" class="br-btn br-btn--primary" :disabled="isSaving || !isFormValid" @click="submitModal">
          {{ isSaving ? 'Saving…' : 'Save Branch' }}
        </button>
      </template>
    </CModal>

    <AppConfirmDialog
      :visible="showDeleteConfirm"
      title="Delete this branch?"
      :message="deleteConfirmMessage"
      confirm-label="Delete"
      cancel-label="Keep"
      busy-label="Deleting…"
      :busy="isDeleting"
      variant="danger"
      :close-on-backdrop="!isDeleting"
      @confirm="confirmDelete"
      @cancel="closeDeleteConfirm"
    />
  </div>
</template>

<script>
import AppInput from '@/components/AppInput.vue'
import AppDropdown from '@/components/AppDropdown.vue'
import BaseTable from '@/components/BaseTable.vue'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'
import OrganisationLogoUploader from '@/views/settings/OrganisationLogoUploader.vue'
import statesApi from '@/api/states.js'
import branchesApi from '@/api/branches.js'

function blankForm () {
  return { id: null, name: '', state_id: null, image_url: '' }
}

export default {
  name: 'SettingsBranches',
  components: { AppInput, AppDropdown, BaseTable, AppConfirmDialog, OrganisationLogoUploader },
  data () {
    return {
      states: [],
      branches: [],
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      filterStateId: null,
      showModal: false,
      isEdit: false,
      isSaving: false,
      modalError: '',
      form: blankForm(),
      showDeleteConfirm: false,
      branchToDelete: null,
      isDeleting: false,
      columns: [
        { key: 'id', label: 'Id', width: '64px', align: 'right' },
        { key: 'image', label: '', width: '60px', sortable: false, align: 'center' },
        { key: 'name', label: 'Branch' },
        { key: 'state', label: 'State', width: '180px' },
        { key: 'actions', label: 'Actions', width: '170px', align: 'right', sortable: false }
      ]
    }
  },
  computed: {
    stateOptions () {
      return this.states.map((s) => ({ value: s.id, label: s.name }))
    },
    stateFilterOptions () {
      return [{ value: null, label: 'All states' }, ...this.stateOptions]
    },
    stateNameById () {
      const map = {}
      this.states.forEach((s) => { map[Number(s.id)] = s.name })
      return map
    },
    modalTitle () {
      return this.isEdit ? 'Edit Branch' : 'Add Branch'
    },
    isFormValid () {
      const nameOk = String(this.form.name || '').trim().length > 0
      const stateOk = !!Number(this.form.state_id)
      return nameOk && stateOk
    },
    tableRows () {
      return this.branches.map((b) => ({
        ...b,
        stateName: b.state?.name || this.stateNameById[Number(b.state_id)] || ''
      }))
    },
    filteredRows () {
      if (!this.filterStateId) return this.tableRows
      return this.tableRows.filter((r) => Number(r.state_id) === Number(this.filterStateId))
    },
    deleteConfirmMessage () {
      const b = this.branchToDelete
      if (!b) return ''
      return `Permanently remove ${b.name}. The server will block this if any users or orders are still assigned to it.`
    }
  },
  created () {
    this.loadAll()
  },
  methods: {
    loadAll () {
      this.loadStates()
      this.loadBranches()
    },
    loadStates () {
      statesApi.list(this.$apiAdress)
        .then((data) => {
          this.states = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message || 'Unable to load states.'
        })
    },
    loadBranches () {
      this.isLoading = true
      this.errorMessage = ''
      branchesApi.list(this.$apiAdress)
        .then((data) => {
          this.branches = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message || 'Unable to load branches.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    openCreateModal () {
      this.form = blankForm()
      this.isEdit = false
      this.modalError = ''
      this.showModal = true
    },
    openEditModal (row) {
      this.form = {
        id: row.id,
        name: row.name,
        state_id: Number(row.state_id),
        image_url: row.image_url || ''
      }
      this.isEdit = true
      this.modalError = ''
      this.showModal = true
    },
    branchInitial (name) {
      if (!name) return '?'
      const trimmed = String(name).trim()
      return trimmed.length ? trimmed.charAt(0).toUpperCase() : '?'
    },
    closeModal () {
      if (this.isSaving) return
      this.showModal = false
      this.form = blankForm()
      this.modalError = ''
    },
    submitModal () {
      if (!this.isFormValid) return
      this.isSaving = true
      this.modalError = ''
      const payload = {
        name: this.form.name.trim(),
        state_id: Number(this.form.state_id),
        image_url: this.form.image_url ? this.form.image_url.trim() : ''
      }
      const request = this.isEdit
        ? branchesApi.update(this.$apiAdress, this.form.id, payload)
        : branchesApi.create(this.$apiAdress, payload)
      request
        .then(() => {
          this.successMessage = this.isEdit ? 'Branch updated.' : 'Branch created.'
          setTimeout(() => { this.successMessage = '' }, 2500)
          this.showModal = false
          this.form = blankForm()
          this.loadBranches()
        })
        .catch((error) => {
          this.modalError = error?.response?.data?.message
            || this.firstValidationError(error?.response?.data?.error)
            || 'Failed to save branch.'
        })
        .finally(() => {
          this.isSaving = false
        })
    },
    openDeleteConfirm (row) {
      this.branchToDelete = row
      this.showDeleteConfirm = true
    },
    closeDeleteConfirm () {
      if (this.isDeleting) return
      this.showDeleteConfirm = false
      this.branchToDelete = null
    },
    confirmDelete () {
      if (!this.branchToDelete) return
      this.isDeleting = true
      branchesApi.remove(this.$apiAdress, this.branchToDelete.id)
        .then(() => {
          this.successMessage = 'Branch deleted.'
          setTimeout(() => { this.successMessage = '' }, 2500)
          this.showDeleteConfirm = false
          this.branchToDelete = null
          this.loadBranches()
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message || 'Failed to delete branch.'
          this.showDeleteConfirm = false
          this.branchToDelete = null
        })
        .finally(() => {
          this.isDeleting = false
        })
    },
    firstValidationError (errorMap) {
      if (!errorMap || typeof errorMap !== 'object') return ''
      const firstKey = Object.keys(errorMap)[0]
      const list = errorMap[firstKey]
      return Array.isArray(list) ? list[0] : ''
    }
  }
}
</script>

<style scoped>
.br-page { padding: 0.25rem 0; }
.br-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  gap: 0.75rem; padding: 0.25rem 0.25rem 1rem; flex-wrap: wrap;
}
.br-toolbar__title { display: flex; align-items: center; gap: 0.65rem; }
.br-toolbar__accent {
  width: 4px; height: 36px; border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}
.br-toolbar h1 {
  margin: 0; font-size: 1.15rem; font-weight: 700;
  color: #1f2a44; letter-spacing: -0.01em; line-height: 1.2;
}
.br-toolbar p { margin: 0; font-size: 0.78rem; color: #6b7280; }
.br-toolbar__actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }
.br-toolbar__actions >>> .btn { border-radius: 8px; font-weight: 600; font-size: 0.82rem; }
.br-toolbar__actions >>> .btn.refresh-btn {
  height: 34px; padding: 0 0.85rem;
  background: #ffffff; border: 1px solid #e5e9f2; color: #1f2a44;
}
.br-alert { margin: 0 0 0.75rem; border-radius: 10px; }
.br-section {
  background: #ffffff; border: 1px solid #e5e9f2;
  border-radius: 12px; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}
.br-section__body { padding: 1rem; }
.br-filters { display: flex; flex-wrap: wrap; gap: 0.85rem; align-items: flex-end; }
.br-filter { display: flex; flex-direction: column; flex: 1 1 240px; min-width: 0; }
.br-label {
  display: block; margin-bottom: 0.3rem;
  font-size: 0.78rem; font-weight: 600; color: #4f5560;
}
.br-req { color: #dc2626; font-weight: 700; }
.br-state-pill {
  display: inline-flex; align-items: center;
  padding: 0.12rem 0.55rem; border-radius: 999px;
  font-size: 0.72rem; font-weight: 700;
  background: #ecfdf5; color: #0e5b46;
  letter-spacing: 0.02em;
}
.br-thumb {
  display: inline-flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; border-radius: 8px;
  background: #ecfdf5; color: #0e5b46;
  border: 1px solid #e5e9f2;
  overflow: hidden;
  font-size: 0.9rem; font-weight: 800;
}
.br-thumb img {
  width: 100%; height: 100%; object-fit: cover;
}
.br-thumb__initial { line-height: 1; }
.br-row-actions {
  display: inline-flex; align-items: center; gap: 0.4rem; justify-content: flex-end;
}
.br-action-btn {
  display: inline-flex; align-items: center; gap: 0.35rem;
  height: 28px; padding: 0 0.7rem; border-radius: 6px;
  border: 1px solid #e5e9f2; background: #ffffff; color: #1f2a44;
  font-size: 0.76rem; font-weight: 600; cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}
.br-action-btn:focus { outline: none; }
.br-action-btn:focus-visible {
  outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}
.br-action-btn--edit:hover {
  border-color: #16a07a; color: #0e5b46; background: #f5fbf8;
  box-shadow: 0 2px 4px rgba(14, 91, 70, 0.1);
}
.br-action-btn--delete:hover {
  border-color: #ef4444; color: #b91c1c; background: #fef2f2;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.12);
}
.br-action-btn--delete:focus-visible {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.30);
}
.br-modal >>> .modal-content {
  border-radius: 14px; border: 1px solid #e5e9f2;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.22); overflow: hidden;
}
.br-modal >>> .modal-header {
  border-bottom: 1px solid #eef1f7; background: #ffffff; padding: 0.95rem 1.15rem;
}
.br-modal >>> .modal-header .modal-title {
  font-size: 1.05rem; font-weight: 700; color: #1f2a44; margin: 0; letter-spacing: -0.01em;
}
.br-modal >>> .modal-body {
  background: #fbfcfe; padding: 1.1rem;
  max-height: calc(100vh - 220px); overflow-y: auto;
}
.br-modal >>> .modal-footer {
  border-top: 1px solid #eef1f7; background: #ffffff; padding: 0.7rem 1.15rem; gap: 0.5rem;
}
.br-modal__body { display: flex; flex-direction: column; gap: 0.85rem; }
.br-modal__alert {
  padding: 0.6rem 0.85rem; background: #fee2e2; color: #991b1b;
  border: 1px solid #fecaca; border-radius: 8px;
  font-size: 0.82rem; font-weight: 600;
}
.br-grid {
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem;
}
.br-field { display: flex; flex-direction: column; min-width: 0; }
.br-field--wide { grid-column: 1 / -1; }
.br-btn {
  display: inline-flex; align-items: center; justify-content: center;
  height: 34px; min-width: 90px; padding: 0.45rem 1.1rem; border-radius: 8px;
  font-family: inherit; font-size: 0.85rem; font-weight: 600; cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease, background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.br-btn:focus { outline: none; }
.br-btn:focus-visible {
  outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}
.br-btn:disabled { cursor: not-allowed; opacity: 0.6; filter: none; transform: none; box-shadow: none; }
.br-btn--ghost {
  background: #ffffff; border: 1px solid #e5e9f2; color: #1f2a44;
}
.br-btn--ghost:hover:not(:disabled) { background: #f8fafc; border-color: #d8dde6; }
.br-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0; color: #ffffff; box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}
.br-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}
@media (max-width: 575px) {
  .br-grid { grid-template-columns: 1fr; }
}
</style>
